// The paywall after a free trial: covers the app until a plan is bought. Hidden only on the plans
// and checkout pages, which is where it sends people; the API closes TRIAL_GRACE_DAYS after the end date.
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { FiArrowRight } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import { usePlan } from "../utils/usePlan";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: none; }
`;
const grow = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

const Screen = styled.div`
  position: fixed;
  inset: 0;
  z-index: 18000; /* under the first-login welcome, over everything else */
  display: grid;
  place-items: center;
  padding: 32px 20px;
  overflow: hidden auto;
  background: ${({ theme }) => (theme.mode === "dark" ? "rgba(6, 10, 18, 0.78)" : "rgba(8, 24, 44, 0.42)")};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-delay: 0ms !important;
    }
  }
`;


const Inner = styled.div`
  position: relative;
  width: min(540px, 100%);
  padding: 40px 36px 32px;
  border-radius: 24px;
  border: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.surface || theme.bg};
  box-shadow: ${({ theme }) => theme.shadowLg};
  text-align: center;
  @media (max-width: 480px) {
    padding: 30px 20px 24px;
  }
  > * {
    opacity: 0;
    animation: ${fadeUp} 0.8s cubic-bezier(0.2, 0.7, 0.2, 1) forwards;
  }
  > :nth-child(1) { animation-delay: 0.1s; }
  > :nth-child(2) { animation-delay: 0.3s; }
  > :nth-child(3) { animation-delay: 0.5s; }
  > :nth-child(4) { animation-delay: 0.7s; }
`;

const Title = styled.h1`
  margin: 0;
  font-family: var(--font-display, "Instrument Serif", Georgia, serif);
  font-weight: 400;
  font-size: clamp(32px, 6vw, 46px);
  line-height: 1.04;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.ink};
  .still {
    display: block;
    font-style: italic;
    color: ${({ theme }) => theme.accent};
  }
`;

const Sub = styled.p`
  margin: 18px auto 0;
  max-width: 420px;
  font-size: 15px;
  line-height: 1.55;
  color: ${({ theme }) => theme.inkSoft || theme.inkMuted};
  b { color: ${({ theme }) => theme.ink}; font-weight: 500; }
`;

// The countdown: four tabular figures, then a thin track from the trial's end to the deletion date.
const Clock = styled.div`
  margin: 30px auto 0;
  max-width: 440px;
  .digits {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    align-items: start;
  }
  .unit {
    position: relative;
    display: grid;
    gap: 2px;
    &:not(:first-child)::before {
      content: ":";
      position: absolute;
      left: 0;
      top: 0;
      transform: translateX(-50%);
      font-size: clamp(24px, 4vw, 32px);
      line-height: 1.1;
      font-weight: 200;
      color: ${({ theme }) => theme.inkFaint};
    }
  }
  .n {
    font-size: clamp(28px, 5vw, 40px);
    line-height: 1.05;
    font-weight: 300;
    letter-spacing: -0.03em;
    font-variant-numeric: tabular-nums;
    color: ${({ theme }) => theme.ink};
    &.zero { color: ${({ theme }) => theme.inkMuted}; }
  }
  .l {
    font-size: 12.5px;
    color: ${({ theme }) => theme.inkMuted};
  }
  .track {
    position: relative;
    height: 2px;
    margin: 26px 0 10px;
    border-radius: 2px;
    background: ${({ theme }) => theme.lineStrong || theme.line};
    overflow: visible;
    .fill {
      position: absolute;
      inset: 0;
      width: ${({ $pct }) => $pct}%;
      border-radius: 2px;
      background: ${({ theme }) => theme.secondary};
      transform-origin: left;
      animation: ${grow} 1.2s cubic-bezier(0.2, 0.7, 0.2, 1) 0.9s both;
    }
    .now {
      position: absolute;
      top: 50%;
      left: ${({ $pct }) => $pct}%;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      transform: translate(-50%, -50%);
      background: ${({ theme }) => theme.secondary};
      box-shadow: 0 0 0 3px ${({ theme }) => theme.surface || theme.bg};
      opacity: 0;
      animation: ${fadeUp} 0.4s ease-out 1.9s forwards;
    }
  }
  .ends {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 12.5px;
    color: ${({ theme }) => theme.inkMuted};
    span:last-child { color: ${({ theme }) => theme.secondary}; }
  }
`;

const Actions = styled.div`
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  .go {
    appearance: none;
    height: 48px;
    padding: 0 26px;
    border-radius: 999px;
    border: 1px solid ${({ theme }) => theme.accent};
    background: ${({ theme }) => theme.accent};
    color: ${({ theme }) => theme.onFill || "#fff"};
    font: inherit;
    font-size: 15.5px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    box-shadow: ${({ theme }) => theme.shadowMd};
    transition: transform 0.15s, background 0.15s;
    svg { transition: transform 0.15s; }
    &:hover {
      background: ${({ theme }) => theme.accentHover || theme.accent};
      svg { transform: translateX(3px); }
    }
    &:active { transform: translateY(1px); }
    &:focus-visible { outline: 2px solid ${({ theme }) => theme.accent}; outline-offset: 3px; }
  }
  .out {
    appearance: none;
    border: 0;
    background: none;
    padding: 4px 8px;
    border-radius: 6px;
    font: inherit;
    font-size: 13.5px;
    color: ${({ theme }) => theme.inkMuted};
    cursor: pointer;
    &:hover { color: ${({ theme }) => theme.ink}; }
    &:focus-visible { outline: 2px solid ${({ theme }) => theme.accent}; outline-offset: 2px; }
  }
`;

// Pages where the wall steps aside so the plan can actually be bought.
const OPEN_PATHS = ["/plans", "/checkout"];

const when = (v) => (v ? new Date(v).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
const shortDay = (v) => (v ? new Date(v).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : null);
const two = (n) => String(n).padStart(2, "0");

// Time left until `until`, split for the clock; all zeros once it has passed.
const split = (until) => {
  const ms = Math.max(0, (until ? new Date(until).getTime() : 0) - Date.now());
  const s = Math.floor(ms / 1000);
  return { days: Math.floor(s / 86400), hours: Math.floor((s % 86400) / 3600), mins: Math.floor((s % 3600) / 60), secs: s % 60, ms };
};

export default function TrialEndedWall() {
  const { status } = usePlan();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const wall = status?.wall;
  const until = wall?.deactivatesAt;
  const [left, setLeft] = useState(() => split(until));

  useEffect(() => {
    setLeft(split(until));
    if (!until) return undefined;
    const t = setInterval(() => setLeft(split(until)), 1000);
    return () => clearInterval(t);
  }, [until]);

  if (!wall || OPEN_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"))) return null;

  const member = status.role === "member";
  const gone = wall.deactivated || (until && left.ms <= 0);
  const ended = when(wall.endedAt);
  const deadline = when(until);
  const start = wall.endedAt ? new Date(wall.endedAt).getTime() : null;
  const end = until ? new Date(until).getTime() : null;
  const pct = start && end && end > start ? Math.min(100, Math.max(0, ((Date.now() - start) / (end - start)) * 100)) : 100;
  const units = [
    ["days", left.days],
    ["hours", left.hours],
    ["minutes", left.mins],
    ["seconds", left.secs],
  ];

  return createPortal(
    <Screen role="dialog" aria-modal="true" aria-labelledby="trial-wall-title">
      <Inner>
        <Title id="trial-wall-title">
          {gone ? "This account is being deleted." : "Your free trial has ended."}
          {!gone && <span className="still">Everything you saved is still here.</span>}
        </Title>
        <Sub>
          {gone ? (
            <>
              No plan was chosen within 30 days of the trial ending{ended ? <> on <b>{ended}</b></> : null}, so this
              account and everything in it is being permanently deleted. Choosing a plan right now is the only way to
              keep it.
            </>
          ) : (
            <>
              {ended ? <>It ended on <b>{ended}</b>. </> : null}
              {member ? (
                <>Ask <b>{status.managedBy || "your account admin"}</b> to choose a plan to keep using scoutm8.</>
              ) : (
                <>Choose a plan to keep using scoutm8.</>
              )}
              {deadline ? <> Without one, this account and everything in it is permanently deleted on <b>{deadline}</b>.</> : null}
            </>
          )}
        </Sub>
        {until && !gone ? (
          <Clock $pct={pct} aria-live="off">
            <div className="digits" aria-label={`${left.days} days, ${left.hours} hours and ${left.mins} minutes left to choose a plan`}>
              {units.map(([label, n]) => (
                <div className="unit" key={label}>
                  <span className={"n" + (n === 0 && label === "days" ? " zero" : "")}>{label === "days" ? n : two(n)}</span>
                  <span className="l">{label}</span>
                </div>
              ))}
            </div>
            <div className="track" aria-hidden="true">
              <span className="fill" />
              <span className="now" />
            </div>
            <div className="ends" aria-hidden="true">
              <span>Trial ended {shortDay(wall.endedAt) || ""}</span>
              <span>Deleted {shortDay(until)}</span>
            </div>
          </Clock>
        ) : null}
        <Actions>
          {!member && (
            <button type="button" className="go" onClick={() => navigate("/plans")}>
              Choose a plan <FiArrowRight size={16} aria-hidden="true" />
            </button>
          )}
          <button type="button" className="out" onClick={() => logout()}>
            Sign out
          </button>
        </Actions>
      </Inner>
    </Screen>,
    document.body,
  );
}
